// Parses runtime source options and manifest hints from DOM source elements.
// It reuses the public option parser so hybrid and runtime stay aligned.
import type { LuisRuntimeOptions } from '../../options'
import { parseLuisRuntimeOptionsAttribute } from '../../options'
import type { LuisSourceElement } from './context'
import { isRuntimeDiagnosticsAllowed } from '@lentystyle/guard/runtime'
import { buildRuntimeSecurityViolation, emitRuntimeSecurityViolation } from '../security-events'

export type { LuisRuntimeOptions } from '../../options'
export type { LuisRuntimeWorkerMode } from '../../options'

type LuisRuntimeStyleOutputMode = 'full' | 'observed-only'

// Read effective runtime options from one source element.
export function readLuisRuntimeOptions(source: LuisSourceElement): LuisRuntimeOptions {
  // Keep the raw attribute value from the current source element.
  const raw = source.getAttribute('data-luis')
  const options = parseLuisRuntimeOptionsAttribute(raw)
  // Return sanitized options so DOM attributes cannot enable production diagnostics.
  return applyRuntimeDiagnosticsPolicy(options)
}

// Force diagnostic options off unless browser policy explicitly allows them.
function applyRuntimeDiagnosticsPolicy(options: LuisRuntimeOptions): LuisRuntimeOptions {
  // Keep parsed options unchanged when diagnostics were explicitly allowed.
  if (isRuntimeDiagnosticsAllowed()) {
    return options
  } else {
    if (options.debug || options.map) {
      emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
        'diagnostics-blocked',
        'Runtime diagnostics were requested but blocked by security policy.',
      ))
    }
    // Return the production-safe option set with debug/map disabled.
    return { ...options, debug: false, map: false }
  }
}

// Read whether preloaded CSS should suppress regular runtime style output.
export function readLuisStyleOutputMode(source: LuisSourceElement): LuisRuntimeStyleOutputMode {
  // Keep the internal hybrid marker isolated from public `data-luis` tokens.
  const raw = source.getAttribute('data-luis-output')
  // Return observed-only mode only for the explicit internal marker.
  if (raw === 'observed-only') {
    return 'observed-only'
  }

  // Return full output for standalone runtime and unknown marker values.
  return 'full'
}

// Read optional manifest attribute used for compile-mode overrides.
export function readLuisManifestAttribute(source: LuisSourceElement): string | null {
  // Keep the raw manifest attribute value from the current source element.
  const raw = source.getAttribute('data-luis-manifest')
  // Return null when manifest attribute is missing.
  if (raw === null) {
    // Return null for missing manifest attributes.
    return null
  } else {
    // Continue manifest value normalization for the current source element.
  }

  // Keep the normalized manifest attribute value.
  const normalized = raw.trim()
  // Return null for empty manifest attribute values.
  if (!normalized) {
    // Return null when the current manifest attribute is empty.
    return null
  } else {
    // Return the normalized manifest attribute value.
    return normalized
  }
}
